// Encryption Service - Encryption/Decryption Logic & Key Management
// Extracted from userDataPersistence.js for better separation of concerns

import { debugLog } from '../debugUtils';
import { detectStorageEnvironment } from './storageDetection';
import {
  cookieHelpers,
  localStorageHelpers,
  sessionStorageHelpers,
} from './storageHelpers';

// Storage keys and configuration
const STORAGE_PREFIX = 'swts_'; // Sing with the Stars prefix
const ENCRYPTION_KEY_KEY = `${STORAGE_PREFIX}enc_key`;

/**
 * Get encryption key based on environment strategy
 */
export const getEncryptionKey = () => {
  const environment = detectStorageEnvironment();

  if (environment.strategy === 'PLAIN_TEXT') {
    debugLog(
      '🔓 Using plain text storage for OTO compatibility:',
      environment.reason
    );
    return null; // No encryption
  }

  try {
    // Try sessionStorage first (better isolation)
    let key = sessionStorageHelpers.get(ENCRYPTION_KEY_KEY);

    if (!key) {
      // Fallback to localStorage or cookies for ANY environment where sessionStorage failed
      try {
        key = localStorageHelpers.get(ENCRYPTION_KEY_KEY);
        if (key) {
          debugLog('🔑 Retrieved encryption key from localStorage fallback');
        }
      } catch (e) {
        // localStorage failed, try cookies
        key = cookieHelpers.get(ENCRYPTION_KEY_KEY);
        if (key) {
          debugLog('🔑 Retrieved encryption key from cookies fallback');
        }
      }
    }

    if (!key) {
      // Generate new key
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      key = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join(
        ''
      );

      // Store key based on what's available
      try {
        sessionStorageHelpers.set(ENCRYPTION_KEY_KEY, key);
        debugLog('🔑 Generated encryption key, stored in sessionStorage');
      } catch (e) {
        try {
          localStorageHelpers.set(ENCRYPTION_KEY_KEY, key);
          debugLog(
            '🔑 Generated encryption key, stored in localStorage (sessionStorage failed)'
          );
        } catch (e2) {
          try {
            cookieHelpers.set(ENCRYPTION_KEY_KEY, key);
            debugLog(
              '🔑 Generated encryption key, stored in cookies (storage failed)'
            );
          } catch (e3) {
            debugLog(
              '🚨 Failed to store encryption key anywhere, using memory only'
            );
            // Key will be lost on page refresh, but still works for single page
          }
        }
      }
    }

    return key;
  } catch (error) {
    debugLog(
      '❌ Failed to get encryption key, falling back to plain text:',
      error
    );
    return null;
  }
};

/**
 * Simple encryption using AES-like operations (for client-side security)
 * Note: This is obfuscation, not cryptographic security - prevents casual tampering
 */
export const encryptValue = (value, key) => {
  if (!value || !key) return value;

  try {
    const valueStr = value.toString();
    let encrypted = '';

    for (let i = 0; i < valueStr.length; i++) {
      const valueChar = valueStr.charCodeAt(i);
      const keyChar = key.charCodeAt(i % key.length);
      const encryptedChar = valueChar ^ keyChar; // XOR encryption
      encrypted += String.fromCharCode(encryptedChar);
    }

    // Base64 encode and add prefix to identify encrypted values
    return 'enc_' + btoa(encrypted);
  } catch (error) {
    debugLog('❌ Encryption failed:', error);
    return value; // Return original value if encryption fails
  }
};

/**
 * Decrypt value using session key
 */
export const decryptValue = (encryptedValue, key) => {
  if (!encryptedValue || !key) return encryptedValue;

  // Check if value is actually encrypted
  if (!encryptedValue.startsWith('enc_')) {
    return encryptedValue; // Not encrypted, return as-is
  }

  try {
    // Remove prefix and decode
    const encrypted = atob(encryptedValue.substring(4));
    let decrypted = '';

    for (let i = 0; i < encrypted.length; i++) {
      const encryptedChar = encrypted.charCodeAt(i);
      const keyChar = key.charCodeAt(i % key.length);
      const decryptedChar = encryptedChar ^ keyChar; // XOR decryption
      decrypted += String.fromCharCode(decryptedChar);
    }

    return decrypted;
  } catch (error) {
    debugLog('❌ Decryption failed for value:', error);
    throw new Error('Decryption failed');
  }
};

/**
 * Clear performance cache - Note: Cache moved to userDataService
 */
export const clearDecryptionCache = () => {
  // Cache management moved to userDataService for better separation
  // This function is kept for API compatibility
};

/**
 * Get encryption strategy info for diagnostics
 */
export const getEncryptionStrategy = () => {
  const environment = detectStorageEnvironment();
  const key = getEncryptionKey();

  return {
    strategy: environment.strategy,
    reason: environment.reason,
    hasEncryptionKey: !!key,
    keySource: key ? 'available' : 'none',
    details: environment.details || {},
    userAgent:
      typeof window !== 'undefined' ? navigator.userAgent : 'server-side',
    timestamp: new Date().toISOString(),
  };
};
